import React, { useState } from 'react';
import { motion } from 'framer-motion';
import TopBanner from '../../components/TopBanner';
import { Link } from 'react-router-dom';
import Accordion from 'react-bootstrap/Accordion';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';

export default function AIMLDevelopment() {
    const homeimg = '/images/service/aiml.png';
    const server_url = process.env.REACT_APP_SERVER;

    // --- Animation Configuration ---
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { 
            opacity: 1, 
            y: 0, 
            transition: { duration: 0.6, ease: "easeOut" } 
        }
    };

    // Trigger animation when 20% of the element is visible
    const scrollConfig = { once: true, amount: 0.2 };

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "AI/ML Development",
        "provider": {
            "@type": "Organization",
            "name": "Lurantech",
            "url": "https://lurantech.com",
            "logo": "https://lurantech.com/images/logo.png",
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+91-7226-999-101",
                "contactType": "Customer Service",
                "areaServed": "IN",
                "availableLanguage": "English"
            },
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "FF-401, 4th Floor, 7 Sky Complex, Laxmipura, Near VUDA Cross Road",
                "addressLocality": "Vadodara",
                "addressRegion": "Gujarat",
                "postalCode": "390021",
                "addressCountry": "IN"
            }
        },
        "description": "Unlock innovation with our AI/ML development services, delivering intelligent, data-driven solutions for enhanced decision-making and efficiency.",
        "serviceAudience": {
            "@type": "Audience",
            "audienceType": "Business Clients"
        },
        "offers": [
            {
                "@type": "Offer",
                "priceCurrency": "INR",
                "price": "Contact for pricing",
                "eligibleRegion": {
                    "@type": "Place",
                    "name": "India"
                },
                "url": "https://lurantech.com/service/ai-ml-development"
            }
        ]
    };

    // Independent accordion state
    const [activeKey, setActiveKey] = useState(null);

    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <meta property="og:title" content="LuranTech | AI/ML Development | Data-Driven Intelligent Solutions" />
                    <meta property="og:description" content="Unlock innovation with LuranTech’s AI/ML development services to deliver intelligent, data-driven solutions." />
                    <meta property="og:image" content={`${server_url}${homeimg}`} />
                    <meta property="og:url" content="https://lurantech.com/service/ai-ml-development" />
                    <meta property="og:type" content="website" />
                    <meta name="robots" content="index, follow" />
                    <meta name="description" content="Unlock innovation with LuranTech’s AI/ML development services to deliver intelligent, data-driven solutions." />
                    <meta name="keywords" content="AI Development, ML Development, Artificial Intelligence, Machine Learning, Data Analytics" />
                    <meta name="author" content="LuranTech" />
                    <link rel="canonical" href="https://lurantech.com/service/ai-ml-development" />
                    <title>LuranTech | AI/ML Development | Data-Driven Intelligent Solutions</title>
                </Helmet>

                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
                
                <TopBanner title="AI/ML Development" />

                <div className="it-sol-con container">
                    {/* Introduction Paragraph */}
                    <motion.p 
                        initial="hidden"
                        whileInView="visible"
                        viewport={scrollConfig}
                        variants={fadeInUp}
                    >
                        At LuranTech, we specialize in providing cutting-edge <strong>AI and ML Development</strong> services that empower businesses to harness the full potential of their data. Our solutions deliver intelligent, data-driven results that streamline operations, enhance decision-making, and improve overall business efficiency. Whether you're looking to integrate AI into your existing systems or build custom ML models, we provide tailored solutions to meet your unique business needs.
                    </motion.p>
                    
                    <br />

                    {/* First Row of Services */}
                    <div className='row g-4'>
                        <motion.div 
                            className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={scrollConfig}
                            variants={fadeInUp}
                        >
                            <section className="service-section">
                                <div className='row g-2 p-2'>
                                    <div className="col-4" style={{textAlign: 'center'}}>
                                        <img src="/images/icons/ai-ml-services-1.png" alt="AI Automation" style={{width: '100px'}} />
                                    </div>
                                    <div className="col-8 d-flex align-items-center justify-content-center">
                                        <h3>AI-Powered Automation</h3>
                                    </div>
                                    <hr />
                                    <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                                        <Accordion.Item eventKey="0">
                                            <Accordion.Header>
                                                {activeKey === "0" ? <ChevronUp /> : <ChevronDown />}
                                                <span className='truncate-2'>Leverage AI to automate complex processes, reducing human intervention and improving operational efficiency.</span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                Leverage AI to automate complex processes, reducing human intervention and improving operational efficiency. From predictive analytics to intelligent decision-making systems, our AI solutions can help your business become more agile and competitive in the digital age.
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                    <div className="col-12 mt-2">
                                        <p><strong>Key Benefits:</strong></p>
                                        <ul>
                                            <li><strong>Process Efficiency: </strong>Reduce time and costs by automating routine processes.</li>
                                            <li><strong>Data-Driven Decisions: </strong>Make faster, more accurate business decisions.</li>
                                            <li><strong>Competitive Edge: </strong>Stay ahead by embracing cutting-edge AI.</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </motion.div>

                        <motion.div 
                            className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={scrollConfig}
                            variants={fadeInUp}
                        >
                            <section className="service-section">
                                <div className='row g-2 p-2'>
                                    <div className="col-4" style={{textAlign: 'center'}}>
                                        <img src="/images/icons/ai-ml-services-2.png" alt="ML Development" style={{width: '100px'}} />
                                    </div>
                                    <div className="col-8 d-flex align-items-center justify-content-center">
                                        <h3>Machine Learning Model Development</h3>
                                    </div>
                                    <hr />
                                    <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                                        <Accordion.Item eventKey="1">
                                            <Accordion.Header>
                                                {activeKey === "1" ? <ChevronUp /> : <ChevronDown />}
                                                <span className='truncate-2'>Our machine learning model development services help you create predictive models tailored to your data.</span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                Whether you're working on classification, regression, or recommendation systems, we provide the expertise to build and deploy custom models that drive actionable insights for your business.
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                    <div className="col-12 mt-2">
                                        <p><strong>Key Benefits:</strong></p>
                                        <ul>
                                            <li><strong>Predictive Analytics: </strong>Forecast trends and outcomes using historical data.</li>
                                            <li><strong>Personalized Recommendations: </strong>Enhance user engagement.</li>
                                            <li><strong>Business Intelligence: </strong>Gain deeper insights into customer behavior.</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </motion.div>
                    </div>

                    <br /><br />

                    {/* Second Row of Services */}
                    <div className='row g-4'>
                        <motion.div 
                            className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={scrollConfig}
                            variants={fadeInUp}
                        >
                            <section className="service-section">
                                <div className='row g-2 p-2'>
                                    <div className="col-4" style={{textAlign: 'center'}}>
                                        <img src="/images/icons/ai-ml-services-3.png" alt="NLP" style={{width: '100px'}} />
                                    </div>
                                    <div className="col-8 d-flex align-items-center justify-content-center">
                                        <h3>Natural Language Processing (NLP)</h3>
                                    </div>
                                    <hr />
                                    <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                                        <Accordion.Item eventKey="2">
                                            <Accordion.Header>
                                                {activeKey === "2" ? <ChevronUp /> : <ChevronDown />}
                                                <span className='truncate-2'>Unlock the power of human language with our NLP services for chatbots and sentiment analysis.</span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                Whether you're building chatbots, sentiment analysis tools, or advanced language models, we leverage AI to enable seamless interaction between machines and humans.
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                    <div className="col-12 mt-2">
                                        <p><strong>Key Benefits:</strong></p>
                                        <ul>
                                            <li><strong>Enhanced Interaction: </strong>Create intelligent, responsive chatbots.</li>
                                            <li><strong>Sentiment Analysis: </strong>Gain insights into customer emotions.</li>
                                            <li><strong>Content Automation: </strong>Categorize text data for improved efficiency.</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </motion.div>

                        <motion.div 
                            className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={scrollConfig}
                            variants={fadeInUp}
                        >
                            <section className="service-section">
                                <div className='row g-2 p-2'>
                                    <div className="col-4" style={{textAlign: 'center'}}>
                                        <img src="/images/icons/ai-ml-services-4.png" alt="Computer Vision" style={{width: '100px'}} />
                                    </div>
                                    <div className="col-8 d-flex align-items-center justify-content-center">
                                        <h3>Computer Vision Solutions</h3>
                                    </div>
                                    <hr />
                                    <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                                        <Accordion.Item eventKey="3">
                                            <Accordion.Header>
                                                {activeKey === "3" ? <ChevronUp /> : <ChevronDown />}
                                                <span className='truncate-2'>Utilize computer vision to analyze visual data and make intelligent decisions automatically.</span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                From image recognition to object detection, we provide AI-driven solutions to automate visual data analysis in various applications across industries.
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                    <div className="col-12 mt-2">
                                        <p><strong>Key Benefits:</strong></p>
                                        <ul>
                                            <li><strong>Image Recognition: </strong>Enable automation in security and manufacturing.</li>
                                            <li><strong>Real-Time Monitoring: </strong>Analyze and act on visual data instantly.</li>
                                            <li><strong>Precision: </strong>Improve accuracy in healthcare and automotive fields.</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </motion.div>
                    </div>

                    <br />

                    {/* Final CTA Section */}
                    <motion.div 
                        initial="hidden"
                        whileInView="visible"
                        viewport={scrollConfig}
                        variants={fadeInUp}
                    >
                        <h2>Why Choose LuranTech for AI/ML Development?</h2>
                        <p>With over 20 years of experience, LuranTech is your trusted partner for AI and ML development. We specialize in building robust, scalable AI/ML solutions that drive innovation and transform your business. Whether you're looking to improve efficiency, make data-driven decisions, or enhance user experiences, our team is equipped with the right expertise and tools to make it happen.</p>
                        <p>We offer end-to-end services including:</p>
                        <ul>
                            <li><strong>AI & ML Strategy: </strong>Assess your goals and implement effective solutions.</li>
                            <li><strong>Custom Model Development: </strong>Create tailored models for your business.</li>
                            <li><strong>Data Analytics: </strong>Unlock valuable insights from your existing data.</li>
                        </ul>
                        <hr />
                        <p>Ready to unlock innovation and improve efficiency with AI/ML? <Link style={{fontWeight:'600', color:'#232323', textDecoration:'underline'}} to='/contact-us'>Contact us today</Link> to discuss how we can help your business grow.</p>
                        <span style={{fontSize:'10px'}}>All icons by <a target="_blank" rel="noreferrer" href="https://icons8.com">Icons8</a></span>
                    </motion.div>
                </div>
            </HelmetProvider>
        </>
    );
}